// Human-friendly text for an HTTP failure when the server did not send a usable JSON body
// (for example an HTML 413 / 500 page from the web server).
export const describeHttpFailure = (status) => {
    if (status === 401 || status === 419) return 'Your session has expired. Please sign in again.';
    if (status === 403) return "You don't have permission to do that.";
    if (status === 404) return 'The requested resource was not found.';
    if (status === 413) return 'The selected files are too large for the server to accept at once. Please upload fewer or smaller images and try again.';
    if (status === 422) return 'Some of the submitted information is not valid. Please check it and try again.';
    if (status === 429) return 'Too many requests. Please wait a moment and try again.';
    if (status >= 500) return `The server ran into a problem (error ${status}). Please try again in a moment.`;
    return `The server sent an unexpected response (error ${status}). Please try again.`;
};

// Like response.json(), but a non-JSON body (HTML error page, empty body, ...) throws an Error
// with a readable message instead of "Unexpected token '<' ... is not valid JSON".
export const readJson = async (response) => {
    const text = await response.text();
    if (!text.trim() && response.ok) return {};
    try {
        const body = JSON.parse(text);
        // Laravel can answer with JSON like { message: "", exception: "PostTooLargeException" }.
        // Give such error bodies a readable message based on the HTTP status.
        if (!response.ok && body && typeof body === 'object' && !Array.isArray(body) && !body.message && !body.error) {
            body.message = describeHttpFailure(response.status);
        }
        return body;
    } catch (e) {
        const error = new Error(describeHttpFailure(response.status));
        error.status = response.status;
        error.friendly = true;
        throw error;
    }
};

// "extra_service_fees.0.name" -> "extra service fees #1 name"
const humanizeFieldNames = (message) =>
    String(message).replace(/\b[a-z_]+(?:\.\d+)?(?:\.[a-z_]+)+\b/g, (token) =>
        token
            .split('.')
            .map((part) => (/^\d+$/.test(part) ? `#${Number(part) + 1}` : part.replace(/_/g, ' ')))
            .join(' '));

const collectMessages = (bag) => {
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return [];
    return Object.values(bag)
        .map((v) => (Array.isArray(v) ? v[0] : v))
        .filter((v) => typeof v === 'string' && v.trim() !== '')
        .map(humanizeFieldNames);
};

// First readable message(s) from an API error body, e.g.
// { message: 'Validation Error.', data:   { email: ['The email has already been taken.'] } }
// { message: 'The given data was invalid.', errors: { pets_allowed: ['...'] } }
export const apiErrorMessage = (body, fallback) => {
    const details = [...collectMessages(body?.errors), ...collectMessages(body?.data)];
    if (details.length) {
        const shown = details.slice(0, 5).join(' ');
        return details.length > 5 ? `${shown} (+${details.length - 5} more)` : shown;
    }
    return body?.message || body?.error || fallback;
};
